import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ChannelErrorTooltip } from '@/modules/campaigns/components/ChannelErrorTooltip';
import { useGoToDialogAction } from '@/modules/tryIt/hooks/useGoToDialogAction';
import { useGoToDialogNode } from '@/modules/tryIt/hooks/useGoToDialogNode';
import { selectIsReplay, selectSessionSource } from '@/modules/tryIt/redux/selectors';
import { isMessageFromCollection, isMessageFromKnowledge } from '@/modules/tryIt/utils/helper';
import { AuthorType } from '@/shared/types/chat';
import { IntegrationType } from '@/shared/types/integration';
import { SynthesisResponseCode } from '@/shared/types/tryIt';

import { AudioResponse } from './AudioResponse';
import { CarouselResponse } from './CarouselResponse';
import { FileResponse } from './FileResponse';
import { GoToResponseButton } from './GoToResponseButton';
import { ImageResponse } from './ImageResponse';
import { QuestionSetResponse } from './QuestionSetResponse';
import { ResponseIcon } from './ResponseIcon';
import { ResponseCard } from './ResponseNodeLabel';
import { SwitchChannelResponse } from './SwitchChannelResponse';
import { TextResponse } from './TextResponse';
import { ResponseMessagesProps } from './types';
import { UrlResponse } from './UrlResponse';
import { VideoResponse } from './VideoResponse';
import { WebviewResponse } from './WebviewResponse';
import { WhatsAppFlowResponse } from './WhatsAppFlowResponse';

export const ResponseMessages = ({
  response,
  onOptionClick,
  onPostback,
  collection,
  onResponseClick,
  canGoToResponse,
  amendPayload,
  correction,
  authorType,
  isWorkflow,
  nodeId,
  isFirst,
  flat = false,
  turnRow = false,
}: ResponseMessagesProps) => {
  const { t } = useTranslation();
  const isInstructionsReply = isMessageFromKnowledge(collection);
  const isCollectionReply = isMessageFromCollection(collection);
  const isCampaignReply = authorType === AuthorType.CAMPAIGN;
  const isBroadcastReply = !isCollectionReply && !response.action_id;
  const isAuthReply = collection?.response_code === SynthesisResponseCode.AUTHENTICATION_CONTINUE;
  const isReplay = useSelector(selectIsReplay);
  const source = useSelector(selectSessionSource);
  const { error_code } = response;
  const channel = source.channel ?? IntegrationType.WHATSAPP;

  // Show the icon slot on the first bubble (category icon) or on any bubble
  // whose delivery failed (error triangle). A failed non-first bubble would
  // otherwise render icon-less and hide the failure.
  const showIcon = turnRow ? Boolean(error_code) : Boolean(isFirst || error_code);

  const icon =
    !showIcon && turnRow ? null : (
      <span className="inline-flex size-4 shrink-0">
        {showIcon && (
          <ResponseIcon
            error_code={error_code}
            channel={channel}
            isInstructionsReply={isInstructionsReply}
            isCollectionReply={isCollectionReply}
            isCampaignReply={isCampaignReply}
            isBroadcastReply={isBroadcastReply}
            collection={collection}
          />
        )}
      </span>
    );

  const isResponseLinkable = canGoToResponse ? canGoToResponse(response.action_id) : true;

  const isNodeResponse = !isCollectionReply && Boolean(isWorkflow);

  const actionLink = useGoToDialogAction(turnRow ? response.action_id : undefined);
  const nodeLink = useGoToDialogNode(turnRow ? nodeId : undefined);
  const nodeHref = isNodeResponse ? (actionLink?.to ?? nodeLink?.to) : undefined;

  const canGoToAction = isNodeResponse && isResponseLinkable;

  const isGenerated = !isNodeResponse || ((isInstructionsReply || isAuthReply) && !nodeHref);

  const goToButton =
    !turnRow && !isReplay && canGoToAction ? (
      <GoToResponseButton onResponseClick={onResponseClick} response={response} flat={flat} />
    ) : null;

  const bubble = ((): ReactNode => {
    switch (response.type) {
      case 'text':
        return (
          <TextResponse
            response={response}
            collection={collection}
            onOptionClick={onOptionClick}
            amendPayload={amendPayload}
            correction={correction}
            icon={icon}
            goToButton={goToButton}
            flat={flat}
            turnRow={turnRow}
          />
        );
      case 'url':
        return (
          <UrlResponse turnRow={turnRow} response={response} icon={icon} goToButton={goToButton} />
        );
      case 'audio':
        return <AudioResponse response={response} icon={icon} />;
      case 'image':
        return (
          <ImageResponse
            turnRow={turnRow}
            responses={[response]}
            authorType={authorType}
            icon={icon}
            goToButton={goToButton}
          />
        );
      case 'video':
        return (
          <VideoResponse
            turnRow={turnRow}
            response={response}
            authorType={authorType}
            icon={icon}
            goToButton={goToButton}
          />
        );
      case 'file':
        return (
          <FileResponse
            turnRow={turnRow}
            response={response}
            authorType={authorType}
            icon={icon}
            goToButton={goToButton}
          />
        );
      case 'carousel':
        return (
          <CarouselResponse
            turnRow={turnRow}
            nodeType={isGenerated ? '' : 'carousel'}
            onGoToNode={canGoToAction ? () => onResponseClick(response.action_id) : undefined}
            nodeHref={nodeHref}
            response={response}
            onPostback={onPostback}
            icon={icon}
            goToButton={goToButton}
          />
        );
      case 'webview':
      case 'survey':
        return <WebviewResponse response={response} icon={icon} goToButton={goToButton} />;
      case 'whatsapp_flow':
        return <WhatsAppFlowResponse turnRow={turnRow} response={response} icon={icon} />;
      case 'question_set':
        return <QuestionSetResponse turnRow={turnRow} response={response} icon={icon} />;
      case 'switch_channel':
        return <SwitchChannelResponse response={response} icon={icon} goToButton={goToButton} />;
      default:
        return null;
    }
  })();

  if (bubble !== null && turnRow && response.type !== 'carousel') {
    const card = (
      <ResponseCard
        type={isGenerated ? '' : response.type}
        onGoToNode={canGoToAction ? () => onResponseClick(response.action_id) : undefined}
        nodeHref={nodeHref}
      >
        {bubble}
      </ResponseCard>
    );

    if (!error_code) {
      return card;
    }

    return (
      <div className="flex w-full flex-col gap-1.5">
        {card}
        <ChannelErrorTooltip channel={channel} status_code={error_code}>
          <span className="w-fit text-sm leading-5 font-normal text-destructive">
            {t('conversation.message_failed')}
          </span>
        </ChannelErrorTooltip>
      </div>
    );
  }

  if (!error_code || bubble === null) {
    return bubble;
  }

  // A failed delivery gets a red "Message failed" label under the bubble,
  // matching the desk chat. The label is offset past the icon slot
  // (size-4 icon + gap-3) so it aligns with the bubble content above it.
  return (
    <div className="mr-auto flex max-w-full flex-col gap-1.5 [&:not(:last-child)]:mb-2">
      {bubble}
      <ChannelErrorTooltip channel={channel} status_code={error_code}>
        <span className="w-fit pl-7 text-sm leading-5 font-normal text-destructive">
          {t('conversation.message_failed')}
        </span>
      </ChannelErrorTooltip>
    </div>
  );
};
